// Batch-06: shared notification bell. Mount in any role navbar. Shows the authoritative
// unread count, lists real notifications, marks read on click, and navigates where the
// frontend has a destination for the key (unknown keys still show + mark read).
import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useNavigate } from 'react-router-dom'
import { Dropdown, Badge, List, Spin, Empty, Button } from 'antd'
import IconButton from '@mui/material/IconButton'
import Icon from '@mui/material/Icon'
import moment from 'moment'
import { useNotifications } from 'src/context/NotificationContext/NotificationContext'
import { iconForKey, routeForKey } from 'src/Common/notificationPresentation'

const NotificationBell = ({ light }) => {
  const navigate = useNavigate()
  const notifications = useNotifications()
  const [open, setOpen] = useState(false)

  // Provider not mounted (e.g. auth screens) — render nothing.
  if (!notifications) return null

  const { unreadCount, items, loading, loadList, markRead, markAllRead } = notifications

  const onVisibleChange = (next) => {
    setOpen(next)
    if (next) loadList()
  }

  const handleItemClick = (item) => {
    if (!item.isRead) markRead(item.recipientId)
    const route = routeForKey(item.key)
    setOpen(false)
    if (route) navigate(route)
  }

  const panel = (
    <div
      style={{
        width: 360,
        maxHeight: 460,
        background: '#fff',
        borderRadius: 8,
        boxShadow: '0 6px 24px rgba(0,0,0,0.12)',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          borderBottom: '1px solid #f0f0f0',
        }}
      >
        <span style={{ fontWeight: 600, color: '#16233f' }}>Notifications</span>
        <Button
          type="link"
          size="small"
          disabled={!items.some((n) => !n.isRead)}
          onClick={markAllRead}
          style={{ padding: 0 }}
        >
          Mark all read
        </Button>
      </div>

      <div style={{ maxHeight: 400, overflowY: 'auto' }}>
        {loading && !items.length ? (
          <div style={{ textAlign: 'center', padding: 24 }}>
            <Spin />
          </div>
        ) : items.length ? (
          <List
            dataSource={items}
            // Key by the unique recipientId. Without this, antd List falls back to
            // item.key (the notification *type*), so two notifications of the same
            // type collided on the same React key (duplicate-key warning).
            rowKey={(item) => item.recipientId}
            renderItem={(item) => (
              <List.Item
                onClick={() => handleItemClick(item)}
                style={{
                  padding: '10px 16px',
                  cursor: 'pointer',
                  background: item.isRead ? '#fff' : '#eef4ff',
                  alignItems: 'flex-start',
                }}
              >
                <div style={{ display: 'flex', gap: 12, width: '100%' }}>
                  <Icon style={{ color: '#3b6fd4', fontSize: 20, marginTop: 2 }}>
                    {iconForKey(item.key)}
                  </Icon>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: item.isRead ? 500 : 600, color: '#16233f' }}>
                      {item.title}
                    </div>
                    <div style={{ fontSize: 13, color: '#5a6b8c', whiteSpace: 'normal' }}>
                      {item.message}
                    </div>
                    <div style={{ fontSize: 11, color: '#98a6bf', marginTop: 2 }}>
                      {item.createdAtUtc ? moment.utc(item.createdAtUtc).local().fromNow() : ''}
                    </div>
                  </div>
                  {!item.isRead ? (
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: '#3b6fd4',
                        marginTop: 6,
                        flexShrink: 0,
                      }}
                    />
                  ) : null}
                </div>
              </List.Item>
            )}
          />
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No notifications"
            style={{ padding: 24 }}
          />
        )}
      </div>
    </div>
  )

  return (
    <Dropdown
      dropdownRender={() => panel}
      trigger={['click']}
      placement="bottomRight"
      open={open}
      onOpenChange={onVisibleChange}
    >
      <Badge
        count={unreadCount}
        overflowCount={99}
        size="small"
        style={{ position: 'absolute', top: '10px', right: '10px' }}
      >
        <IconButton color="inherit">
          <Icon className={light ? 'text-white' : 'text-dark'}>notifications</Icon>
        </IconButton>
      </Badge>
    </Dropdown>
  )
}

NotificationBell.propTypes = {
  light: PropTypes.bool,
}

export default NotificationBell
